import { ButtonBase, styled, Typography } from "@mui/material";
import React, { useMemo } from "react";
import TestsFilters from "../../common/filters/TestsFilters";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";

type WorkFilterSelectorProps = {
  handleParams: (filters: string[]) => void;
  filters: {
    testsFilters?: string;
  };
  workSuggest?: boolean;
};

const WorkFilterSelector: React.FC<WorkFilterSelectorProps> = ({
  handleParams,
  filters,
  workSuggest,
}) => {
  const { isCollab } = useSelector((state: any) => state.auth);
  const params = useParams();
  const { data: filtersData, loading: filtersLoading } = useFetch(
    `filters/all${!isCollab && params.id ? `?person.id=${params.id}` : ""}`
  );

  const matchers = useMemo(() => {
    if (!filtersData)
      return {
        testsMatcher: [],
        skillsMatcher: [],
        allMatchers: [],
        canMatchSkills: false,
        canMatchTests: false,
        canMatch: false,
      };

    const matchers: { code: string; canSelect: boolean }[] =
      filtersData?.data?.matchers;
    const testsKeys = ["personality", "mood", "dynamics"];
    const skillsKeys = ["technical_skills", "transversal_skills"];

    const activeMatchers = matchers.filter((matcher) => matcher.canSelect);

    const testsMatcher = activeMatchers
      .filter((matcher) => testsKeys.includes(matcher.code))
      .map((el) => el.code);
    const skillsMatcher = activeMatchers
      .filter((matcher) => skillsKeys.includes(matcher.code))
      .map((el) => el.code);

    return {
      testsMatcher,
      skillsMatcher,
      allMatchers: activeMatchers.map((el) => el.code),
      canMatchSkills: skillsMatcher.length > 0,
      canMatchTests: testsMatcher.length > 0,
      canMatch: activeMatchers.length > 0,
    };
  }, [filtersData]);

  const testsFilters = useMemo(() => {
    try {
      return JSON.parse(filters.testsFilters || "[]");
    } catch {
      return [];
    }
  }, [filters.testsFilters]);

  const onChange = (filters: string[], value: boolean) => {
    const testsFilters = value ? filters : [];
    handleParams(testsFilters);
  };

  return (
    <Root>
      {matchers.canMatchSkills && (
        <SelectorItem
          title="Matching par les expériences"
          subtitle="(ex, technique, savoir-faire)"
          filters={matchers.skillsMatcher}
          onChange={onChange}
          isSelected={
            testsFilters.length === matchers.skillsMatcher.length &&
            matchers.skillsMatcher.every((filter: string) =>
              testsFilters.includes(filter)
            )
          }
        />
      )}
      {matchers.canMatchTests && (
        <SelectorItem
          title="Matching par les tests"
          subtitle="(ex, personnalité, dynamique, mood)"
          filters={matchers.testsMatcher}
          onChange={onChange}
          isSelected={
            testsFilters.length === matchers.testsMatcher.length &&
            matchers.testsMatcher.every((filter: string) =>
              testsFilters.includes(filter)
            )
          }
        />
      )}
      {matchers.canMatch &&
        matchers.canMatchSkills &&
        matchers.canMatchTests && (
          <SelectorItem
            title="Matching global"
            subtitle="Compétences, ..."
            filters={matchers.allMatchers}
            onChange={onChange}
            isSelected={
              testsFilters.length === matchers.allMatchers.length &&
              matchers.allMatchers.every((filter: string) =>
                testsFilters.includes(filter)
              )
            }
          />
        )}
      {workSuggest && (
        <TestsFilters
          activeFiltersLoading={filtersLoading}
          activeFilters={filtersData}
          httpFilters={filters}
          submit={handleParams}
        />
      )}
    </Root>
  );
};

export default WorkFilterSelector;

const Root = styled("div")`
  display: flex;
  gap: 12px;
  margin-bottom: 10px;
`;

const SelectorItem: React.FC<{
  isSelected?: boolean;
  title: string;
  subtitle: string;
  filters: string[];
  onChange: (filters: string[], value: boolean) => void;
}> = ({ isSelected = false, title, subtitle, filters, onChange }) => {
  return (
    <SelectorRoot
      className={`${isSelected ? "selected" : ""}`}
      onClick={onChange.bind(this, filters, !isSelected)}
    >
      <Typography variant="body1">{title}</Typography>
      {/* <Typography variant="body2" sx={{ opacity: 0.7 }}>
        {subtitle}
      </Typography> */}
    </SelectorRoot>
  );
};

const SelectorRoot = styled(ButtonBase)(({ theme }: any) => ({
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  borderRadius: "8px",
  padding: "8px 16px",
  background: "none",
  textAlign: "left",
  minWidth: "200px",
  cursor: "pointer",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "flex-start",

  "&:focus": {
    outline: `1px solid ${theme.palette.primary.main}`,
    boxShadow: "none",
  },

  "&.selected": {
    backgroundColor: theme.palette.primary.main,
    color: "white",
  },
}));
